/**
 * AdminAccount Page Component
 * Administrative dashboard view for managing admin profile information, viewing system audit logs,
 * navigating admin sub-portals, and executing authenticated session logout.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import LogoutConfirmModal from '../../Components/Layout/LogoutConfirmModal';
import DeleteAdminAccountConfirmModal from '../../Components/Layout/DeleteAdminAccountConfirmModal';
import { getAuditLogEntries, clearAuditLogEntries, getClearHistory } from '../../utils/auditLog';
import { clearLoginPrefill, saveLoginPrefill } from '../../utils/loginPrefill';
import { resolveAdminRole } from '../../utils/adminRole';
import { fetchWithBackendFallback } from '../../utils/backendApi';
import '../../assets/fontello-plus/fontello-73784b09/css/calander.css';
import '../../assets/fontello-change/fontello-79be5077/css/fontello.css';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

const AdminAccount = () => {
  const navigate = useNavigate();
  const [adminInfo, setAdminInfo] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [loggingOut, setLoggingOut] = useState(false);
  const [auditLogEntries, setAuditLogEntries] = useState([]);
  const [showClearAuditConfirm, setShowClearAuditConfirm] = useState(false);
  const [clearHistory, setClearHistory] = useState([]);
  const [showClearHistory, setShowClearHistory] = useState(false);
  const [logoutModalStep, setLogoutModalStep] = useState(null);

  // ── Delete account state ────────────────────────────────────────────────────
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  // ── Guard: must be logged in AND be an admin ─────────────────────────────────
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();

        if (!session) {
          navigate('/login');
          return;
        }

        const { data: isAdmin, error } = await supabase.rpc('check_is_admin', {
          p_email: session.user.email,
        });

        if (error || !isAdmin) {
          // Logged in but not an admin — send to normal account
          navigate('/account');
          return;
        }

        await resolveAdminRole(session.user.email);

        setAdminInfo({
          email:       session.user.email,
          lastSignIn:  session.user.last_sign_in_at,
          createdAt:   session.user.created_at,
        });

        setAuditLogEntries(getAuditLogEntries());
        setClearHistory(getClearHistory());
      } catch (err) {
        console.error('Admin account load error:', err);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    checkAdmin();
  }, [navigate]);

  // Keep the audit log display up-to-date without requiring a full page reload.
  // 'audit-log-updated' fires in the same tab whenever appendAuditLogEntry writes a new entry.
  // The browser 'storage' event fires when another tab changes the same localStorage key.
  // 'visibilitychange' catches the case where the admin switches browser tabs and comes back.
  useEffect(() => {
    const refresh = () => {
      setAuditLogEntries(getAuditLogEntries());
      setClearHistory(getClearHistory());
    };

    const onStorage = (e) => {
      if (e.key === 'clipstone_admin_audit_log' || e.key === 'clipstone_admin_audit_clear_history') {
        refresh();
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    window.addEventListener('audit-log-updated', refresh);
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('audit-log-updated', refresh);
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const performSignOut = async (shouldSaveDetails) => {
    setLoggingOut(true);

    if (shouldSaveDetails) {
      saveLoginPrefill({
        email: adminInfo?.email,
        username: '',
      });
    } else {
      clearLoginPrefill();
    }

    await supabase.auth.signOut();
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('isAdmin');
    setLogoutModalStep(null);
    navigate('/login');
  };

  const handleSignOut = () => {
    setLogoutModalStep('confirm');
  };

  // ── Delete admin account ────────────────────────────────────────────────────
  // Permanently removes the admin row from tbl_admin and deletes the Supabase auth
  // identity (if service-role key is configured). Does NOT call /users/me. ("account-deletion")
  const handleDeleteAccount = async () => {
    setDeleteError('');
    setDeleting(true);
    try {
      await fetchWithBackendFallback('/admin/me', {
        method: 'DELETE',
        requireAuth: true,
      });

      await supabase.auth.signOut().catch(() => {});
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('isAdmin');
      clearLoginPrefill();
      setDeleteModalOpen(false);
      navigate('/login');
    } catch (err) {
      console.error('Admin delete account error:', err);
      setDeleteError(err.message || 'Unable to delete your account right now. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  const formatDateTime = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleClearAuditLog = () => {
    clearAuditLogEntries(adminInfo?.email);
    setAuditLogEntries([]);
    setClearHistory(getClearHistory());
    setShowClearAuditConfirm(false);
  };

  // ── Loading ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.logoRow}>
            <img src={logo} alt="Logo" style={styles.logo} />
          </div>
          <div style={styles.centreBox}>
            <div style={styles.spinner} />
            <p style={styles.mutedText}>Loading admin dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <div style={styles.card}>

        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logo} />
        </div>

        {/* Header */}
        <div style={styles.header}>
          <div style={styles.adminBadge}>Admin</div>
          <h2 style={styles.title}>Admin Dashboard</h2>
          <p style={styles.subtitle}>Clipstone Miners Welfare</p>
        </div>

        {/* Admin info */}
        <div style={styles.infoGrid}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Email</span>
            <span style={styles.infoValue}>{adminInfo?.email}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Last Sign In</span>
            <span style={styles.infoValue}>{formatDate(adminInfo?.lastSignIn)}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Account Created</span>
            <span style={styles.infoValue}>{formatDate(adminInfo?.createdAt)}</span>
          </div>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Role</span>
            <span style={styles.roleBadge}><i className="icon-calendar" style={styles.badgeIcon} /> Administrator</span>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <div style={styles.sectionHeading}>Manage staff</div>
          <p style={styles.sectionText}>Manage trustees and social staff, and create admin access for trusted team members.</p>
          <div style={styles.staffToolsActions}>
            <button style={styles.primaryBtn} onClick={() => navigate('/admin/trustees')}>
              <i className="icon-users" style={styles.actionIcon} />
              Manage trustees
            </button>
            <button style={styles.socialStaffBtn} onClick={() => navigate('/admin/social-staff')}>
              Manage social staff
            </button>
            <button style={styles.createAdminBtn} onClick={() => navigate('/admin/create-account')}>
              Create admin accounts
            </button>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <div style={styles.sectionHeading}>Manage website content</div>
          <p style={styles.sectionText}>Update the information boxes and content panels used across the Trust and Social sections from one place.</p>
          <div style={styles.websiteContentActions}>
            <button style={styles.websiteContentBtn} onClick={() => navigate('/admin/site-content')}>
              <i className="icon-paper" style={styles.actionIcon} />
              Manage content boxes
            </button>
          </div>
        </div>

        <div style={styles.sectionCard}>
          <div style={styles.auditHeadingRow}>
            <div style={styles.sectionHeading}>Audit log</div>
            <div style={styles.auditHeadingBtns}>
              {clearHistory.length > 0 && (
                <button type="button" style={styles.viewHistoryBtn} onClick={() => setShowClearHistory((v) => !v)}>
                  {showClearHistory ? 'Hide history' : 'View history'}
                </button>
              )}
              {auditLogEntries.length > 0 && (
                showClearAuditConfirm ? (
                  <div style={styles.clearAuditConfirmWrap}>
                    <span style={styles.clearAuditConfirmText}>Clear all entries?</span>
                    <button type="button" style={styles.clearAuditDangerBtn} onClick={handleClearAuditLog}>
                      Yes, clear
                    </button>
                    <button type="button" style={styles.clearAuditCancelBtn} onClick={() => setShowClearAuditConfirm(false)}>
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button type="button" style={styles.clearAuditBtn} onClick={() => setShowClearAuditConfirm(true)}>
                    Clear log
                  </button>
                )
              )}
            </div>
          </div>
          <p style={styles.sectionText}>Tracks admin changes like creating/updating/deleting events, trustees, social staff, and admin accounts.</p>

          {showClearHistory && (
            <div style={styles.clearHistoryPanel}>
              <div style={styles.clearHistoryHeading}>Deleted log history</div>
              {clearHistory.map((event) => (
                <div key={event.id} style={styles.clearHistoryBatch}>
                  <div style={styles.clearHistoryBatchHeader}>
                    <span style={styles.clearHistoryBatchTitle}>Cleared on {formatDateTime(event.timestamp)}</span>
                    <span style={styles.clearHistoryBatchCount}>{(event.deletedEntries || []).length} entries</span>
                  </div>
                  <div style={styles.auditMeta}>Cleared by: {event.actorEmail || 'Unknown admin'}</div>
                  {(event.deletedEntries || []).length === 0 ? (
                    <p style={styles.clearHistoryEmpty}>No entries were recorded at the time of clearing.</p>
                  ) : (
                    (event.deletedEntries || []).map((entry) => (
                      <div key={entry.id} style={styles.clearHistoryEntry}>
                        <div style={styles.clearHistoryEntryTop}>
                          <span style={styles.clearHistoryEntryTitle}>{entry.action} {entry.entityType}</span>
                          <span style={entry.outcome === 'error' ? styles.auditStatusError : styles.auditStatusSuccess}>
                            {entry.outcome === 'error' ? 'Error' : 'Success'}
                          </span>
                        </div>
                        <div style={styles.auditMeta}>Target: {entry.entityName || 'Unnamed'}</div>
                        <div style={styles.auditMeta}>When: {formatDateTime(entry.timestamp)}</div>
                        {entry.details ? <div style={styles.auditMeta}>Details: {entry.details}</div> : null}
                      </div>
                    ))
                  )}
                </div>
              ))}
            </div>
          )}

          {auditLogEntries.length === 0 ? (
            <p style={styles.mutedText}>No audit entries yet.</p>
          ) : (
            <div style={auditLogEntries.length >= 2 ? styles.auditListScrollable : styles.auditList}>
              {auditLogEntries.map((entry) => (
                <div key={entry.id} style={styles.auditItem}>
                  <div style={styles.auditRowTop}>
                    <span style={styles.auditTitle}>{entry.action} {entry.entityType}</span>
                    <span style={entry.outcome === 'error' ? styles.auditStatusError : styles.auditStatusSuccess}>
                      {entry.outcome === 'error' ? 'Error' : 'Success'}
                    </span>
                  </div>
                  <div style={styles.auditMeta}>Who: {entry.actorEmail || 'Unknown admin'}</div>
                  <div style={styles.auditMeta}>When: {formatDateTime(entry.timestamp)}</div>
                  <div style={styles.auditMeta}>Target: {entry.entityName || 'Unnamed'}</div>
                  {entry.details ? <div style={styles.auditMeta}>Details: {entry.details}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div style={styles.actions}>
          <button
            style={styles.primaryBtn}
            onClick={() => navigate('/admin/events')}
          >
            <i className="icon-calendar" style={styles.actionIcon} />
            Manage Events
          </button>
          <button
            style={styles.contactDetailsBtn}
            onClick={() => navigate('/admin/contact-details')}
          >
            Edit contact details
          </button>
          <button
            style={styles.secondaryBtn}
            onClick={() => navigate('/admin/events')}
          >
            <i className="icon-calendar" style={styles.actionIcon} />
            View Booking Requests
          </button>
          <button
            style={styles.secondaryBtn}
            onClick={() => navigate('/')}
          >
            <i className="icon-left" style={styles.actionIcon} />
            Back to Site
          </button>
          <button
            style={loggingOut
              ? { ...styles.signOutBtn, opacity: 0.6, cursor: 'not-allowed' }
              : styles.signOutBtn}
            onClick={handleSignOut}
            disabled={loggingOut}
          >
            {loggingOut ? 'Signing out...' : <><i className="icon-left" style={styles.actionIcon} /> Sign Out</>}
          </button>
        </div>

        {/* Danger Zone */}
        <div style={styles.dangerZone}>
          <div style={styles.dangerZoneTitle}>Danger Zone</div>
          <p style={styles.sectionText}>
            Permanently deletes your admin account and removes your access. This cannot be undone.
          </p>
          {deleteError && <p style={styles.deleteError}>{deleteError}</p>}
          <button
            id="admin-delete-account"
            style={styles.deleteAccountBtn}
            onClick={() => setDeleteModalOpen(true)}
          >
            Delete My Account
          </button>
        </div>

      </div>

      <LogoutConfirmModal
        isOpen={Boolean(logoutModalStep)}
        step={logoutModalStep}
        userEmail={adminInfo?.email || ''}
        isSubmitting={loggingOut}
        onCancel={() => {
          if (!loggingOut) setLogoutModalStep(null);
        }}
        onContinue={() => setLogoutModalStep('save-details')}
        onSaveAndLogout={() => performSignOut(true)}
        onLogoutWithoutSaving={() => performSignOut(false)}
      />

      <DeleteAdminAccountConfirmModal
        isOpen={deleteModalOpen}
        userEmail={adminInfo?.email || ''}
        isSubmitting={deleting}
        error={deleteError}
        onCancel={() => {
          if (!deleting) {
            setDeleteModalOpen(false);
            setDeleteError('');
          }
        }}
        onConfirm={handleDeleteAccount}
      />
    </div>
  );
};

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 'clamp(16px, 4vw, 40px) clamp(12px, 3vw, 20px)',
    background: 'linear-gradient(135deg, #0f1f18 0%, #1e3f32 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '16px',
    padding: 'clamp(18px, 4vw, 40px)',
    width: '100%',
    maxWidth: '960px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
  },
  logo: {
    height: 'clamp(60px, 12vw, 80px)',
    width: 'auto',
  },
  centreBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '32px 0',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #2d4f3c',
    borderTop: '4px solid #4ade80',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  mutedText: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  header: {
    textAlign: 'center',
    marginBottom: '28px',
  },
  adminBadge: {
    display: 'inline-block',
    background: '#4ade80',
    color: '#0f1f18',
    fontSize: '11px',
    fontWeight: '800',
    letterSpacing: '0.1em',
    padding: '4px 12px',
    borderRadius: '20px',
    marginBottom: '10px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 4px 0',
  },
  subtitle: {
    color: '#6b9e82',
    fontSize: '14px',
    margin: 0,
  },
  infoGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '28px',
  },
  sectionCard: {
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '12px',
    padding: '18px',
    marginBottom: '20px',
  },
  sectionHeading: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '6px',
  },
  sectionText: {
    color: '#6b9e82',
    fontSize: '13px',
    margin: '0 0 14px 0',
    lineHeight: 1.5,
  },
  staffToolsActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  websiteContentActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  auditHeadingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
  },
  auditHeadingBtns: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  viewHistoryBtn: {
    padding: '8px 14px',
    background: 'transparent',
    color: '#6b9e82',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
  },
  clearHistoryPanel: {
    background: '#0f1f18',
    border: '1px solid #2d4f3c',
    borderRadius: '10px',
    padding: '14px',
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  clearHistoryHeading: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: '700',
    marginBottom: '2px',
  },
  clearHistoryBatch: {
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '10px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  clearHistoryBatchHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '4px',
  },
  clearHistoryBatchTitle: {
    color: '#fbbf24',
    fontSize: '12px',
    fontWeight: '700',
  },
  clearHistoryBatchCount: {
    color: '#6b9e82',
    fontSize: '11px',
    border: '1px solid #2d4f3c',
    borderRadius: '999px',
    padding: '1px 8px',
  },
  clearHistoryEntry: {
    background: '#1a2e25',
    border: '1px solid #2d4f3c',
    borderRadius: '6px',
    padding: '8px 10px',
  },
  clearHistoryEntryTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '4px',
    flexWrap: 'wrap',
  },
  clearHistoryEntryTitle: {
    color: '#d1fae5',
    fontSize: '12px',
    fontWeight: '700',
  },
  clearHistoryEmpty: {
    color: '#6b9e82',
    fontSize: '12px',
    margin: 0,
  },
  auditListScrollable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '8px',
    maxHeight: '340px',
    overflowY: 'auto',
    paddingRight: '4px',
  },
  clearAuditBtn: {
    background: 'transparent',
    border: '1px solid #2d4f3c',
    color: '#6b9e82',
    borderRadius: '8px',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  clearAuditConfirmWrap: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  clearAuditConfirmText: {
    color: '#fbbf24',
    fontSize: '12px',
    fontWeight: '600',
  },
  clearAuditDangerBtn: {
    background: '#991b1b',
    border: '1px solid #fca5a5',
    color: '#ffffff',
    borderRadius: '8px',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: '700',
  },
  clearAuditCancelBtn: {
    background: 'transparent',
    border: '1px solid #2d4f3c',
    color: '#6b9e82',
    borderRadius: '8px',
    padding: '6px 10px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  auditList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginTop: '8px',
  },
  auditItem: {
    background: '#0f1f18',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '10px 12px',
  },
  auditRowTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '6px',
    flexWrap: 'wrap',
  },
  auditTitle: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '700',
  },
  auditStatusSuccess: {
    color: '#4ade80',
    border: '1px solid #14532d',
    background: '#0f2a1a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700',
  },
  auditStatusError: {
    color: '#f87171',
    border: '1px solid #7f1d1d',
    background: '#3a1a1a',
    borderRadius: '999px',
    padding: '2px 8px',
    fontSize: '11px',
    fontWeight: '700',
  },
  auditMeta: {
    color: '#6b9e82',
    fontSize: '12px',
    marginTop: '2px',
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    rowGap: '8px',
    background: '#152318',
    border: '1px solid #2d4f3c',
    borderRadius: '8px',
    padding: '12px 16px',
  },
  infoLabel: {
    color: '#6b9e82',
    fontSize: '13px',
    fontWeight: '500',
  },
  infoValue: {
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: '600',
    textAlign: 'right',
    maxWidth: '100%',
    wordBreak: 'break-word',
  },
  roleBadge: {
    background: '#14532d',
    color: '#4ade80',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '700',
    border: '1px solid #4ade80',
  },
  badgeIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '6px',
    verticalAlign: 'middle',
    fontFamily: 'calander, fontello',
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  primaryBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(20, 184, 166, 0.18)',
  },
  socialStaffBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #2563eb 0%, #60a5fa 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(37, 99, 235, 0.18)',
  },
  createAdminBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(124, 58, 237, 0.18)',
  },
  websiteContentBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 100%)',
    color: '#0f172a',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '800',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(245, 158, 11, 0.18)',
  },
  contactDetailsBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #1d4ed8 0%, #38bdf8 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(29, 78, 216, 0.18)',
  },
  secondaryBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
    color: '#f8fafc',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(15, 23, 42, 0.18)',
  },
  signOutBtn: {
    padding: '14px',
    background: 'linear-gradient(135deg, #991b1b 0%, #dc2626 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 10px 20px rgba(220, 38, 38, 0.18)',
  },
  actionIcon: {
    display: 'inline-block',
    fontSize: '1.05em',
    marginRight: '8px',
    verticalAlign: 'middle',
    fontFamily: 'calander, fontello',
  },
  dangerZone: {
    marginTop: '20px',
    padding: '18px',
    border: '1px solid #7f1d1d',
    borderRadius: '12px',
    background: '#1a0f0f',
  },
  dangerZoneTitle: {
    color: '#f87171',
    fontSize: '15px',
    fontWeight: '700',
    marginBottom: '6px',
  },
  deleteAccountBtn: {
    padding: '12px 18px',
    background: 'linear-gradient(135deg, #7f1d1d 0%, #dc2626 100%)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(220,38,38,0.25)',
  },
  deleteError: {
    color: '#f87171',
    fontSize: '13px',
    margin: '0 0 12px 0',
  },
};

// Spinner keyframes
const styleTag = document.createElement('style');
styleTag.textContent = `@keyframes spin { to { transform: rotate(360deg); } }`;
document.head.appendChild(styleTag);

export default AdminAccount;